import { useEffect, useId, useRef, useState } from "react";
import { Clock3, LoaderCircle } from "lucide-react";
import { apiBase } from "./lib/api";
import { getDeviceId } from "./lib/storage";
import "./HourlyTraffic.css";

interface Props {
  routeId: string;
  baselineSeconds?: number;
}
type Congestion = "normal" | "busy" | "heavy";
interface Hour {
  hour: number;
  available: boolean;
  averageSeconds: number | null;
  delaySeconds: number | null;
  congestion: Congestion | null;
  sampleCount: number;
  driverCount: number;
  observedDays: number;
}
interface HourlyData {
  routeId: string;
  timezone: "Asia/Jerusalem";
  dayOfWeek: number;
  windowDays: number;
  minimumDrivers: number;
  baselineSeconds: number;
  hours: Hour[];
}
type LoadState =
  | { key: string; status: "loading" | "error" }
  | { key: string; status: "ready"; data: HourlyData };

const dayNames = ["יום שני", "יום שלישי", "יום רביעי", "יום חמישי", "יום שישי", "יום שבת", "יום ראשון"];
const dayOrder = [7, 1, 2, 3, 4, 5, 6];
const allHours = Array.from({ length: 24 }, (_, hour) => hour);
const congestionNames: Record<Congestion, string> = { normal: "תנועה שוטפת", busy: "עומס בינוני", heavy: "עומס כבד" };
const clockLabel = (hour: number) => `${String(hour).padStart(2, "0")}:00`;
const durationLabel = (seconds: number) => `${Math.max(1, Math.round(seconds / 60))} דק׳`;
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const nonnegative = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0;
const count = (value: unknown): value is number => nonnegative(value) && Number.isInteger(value);

function localNow() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jerusalem", weekday: "short", hour: "numeric", hourCycle: "h23",
  }).formatToParts(new Date());
  const weekday = parts.find(part => part.type === "weekday")?.value;
  return {
    day: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(weekday ?? "Mon") + 1,
    hour: Number(parts.find(part => part.type === "hour")?.value ?? "0"),
  };
}

/** Reject mismatched or incomplete aggregates rather than inventing observations. */
function parseData(value: unknown, routeId: string, day: number): HourlyData {
  if (!record(value) || value.routeId !== routeId || value.dayOfWeek !== day ||
      value.timezone !== "Asia/Jerusalem" || value.windowDays !== 56 ||
      !count(value.minimumDrivers) || value.minimumDrivers < 3 || !nonnegative(value.baselineSeconds) ||
      !Array.isArray(value.hours) || value.hours.length !== 24) throw new Error("Invalid aggregate");
  const seen = new Set<number>();
  for (const hour of value.hours) {
    if (!record(hour) || !count(hour.hour) || hour.hour > 23 || seen.has(hour.hour) ||
        typeof hour.available !== "boolean" || !count(hour.sampleCount) || !count(hour.driverCount) ||
        !count(hour.observedDays)) throw new Error("Invalid hour");
    if (hour.available) {
      if (!nonnegative(hour.averageSeconds) || hour.averageSeconds === 0 || !nonnegative(hour.delaySeconds) ||
          !["normal", "busy", "heavy"].includes(String(hour.congestion)) ||
          hour.driverCount < value.minimumDrivers || hour.sampleCount < hour.driverCount || hour.observedDays < 1)
        throw new Error("Insufficient observations");
    } else if (hour.averageSeconds !== null || hour.delaySeconds !== null || hour.congestion !== null) {
      throw new Error("Unknown hour has observations");
    }
    seen.add(hour.hour);
  }
  return { ...(value as unknown as HourlyData), hours: [...value.hours].sort((a, b) => a.hour - b.hour) };
}

/** Content only: the parent provides the dialog heading and its back/close button. */
export default function HourlyTraffic({ routeId, baselineSeconds }: Props) {
  const id = useId();
  const [initial] = useState(localNow);
  const [day, setDay] = useState(initial.day);
  const [hour, setHour] = useState(initial.hour);
  const [expanded, setExpanded] = useState(false);
  const [retry, setRetry] = useState(0);
  const key = `${routeId}:${day}`;
  const [state, setState] = useState<LoadState>({ key, status: "loading" });
  const generation = useRef(0);

  useEffect(() => {
    const request = ++generation.current;
    const controller = new AbortController();
    setState({ key, status: "loading" });
    const timer = setTimeout(() => {
      controller.abort();
      if (generation.current === request) setState({ key, status: "error" });
    }, 25_000);
    void (async () => {
      try {
        if (!routeId.trim()) throw new Error("Missing route");
        const query = new URLSearchParams({ routeId, dayOfWeek: String(day) });
        const response = await fetch(`${apiBase}/api/traffic/hourly?${query}`, {
          method: "GET", cache: "no-store", credentials: "same-origin", signal: controller.signal,
          headers: { "X-Device-Id": getDeviceId() },
        });
        if (!response.ok) throw new Error("Unavailable");
        const data = parseData(await response.json(), routeId, day);
        if (generation.current === request && !controller.signal.aborted) setState({ key, status: "ready", data });
      } catch {
        if (generation.current === request && !controller.signal.aborted) setState({ key, status: "error" });
      } finally { clearTimeout(timer); }
    })();
    return () => { ++generation.current; clearTimeout(timer); controller.abort(); };
  }, [routeId, day, retry, key]);

  const current = state.key === key ? state : { key, status: "loading" as const };
  const data = current.status === "ready" ? current.data : null;
  const selected = data?.hours[hour];
  const maximum = Math.max(1, ...(data?.hours.map(item => item.averageSeconds ?? 0) ?? []));
  const baseline = data?.baselineSeconds || (nonnegative(baselineSeconds) ? baselineSeconds : 0);

  return <section className="hourly-traffic" dir="rtl" aria-label="עומס לפי יום ושעה">
    <div className="hourly-traffic-pickers">
      <label htmlFor={`${id}-day`}>יום בשבוע
        <select id={`${id}-day`} value={day} onChange={event => setDay(Number(event.target.value))}>
          {dayOrder.map(item => <option key={item} value={item}>{dayNames[item - 1]}</option>)}
        </select>
      </label>
      <label htmlFor={`${id}-hour`}>שעת יציאה
        <select id={`${id}-hour`} value={hour} onChange={event => setHour(Number(event.target.value))}>
          {allHours.map(item => <option key={item} value={item}>{clockLabel(item)}</option>)}
        </select>
      </label>
    </div>

    <div className="hourly-traffic-result" aria-live="polite" aria-atomic="true" aria-busy={current.status === "loading"}>
      {current.status === "loading" && <p className="hourly-traffic-loading" role="status"><LoaderCircle size={18} className="spin" aria-hidden="true" />טוענים נתוני נסיעות…</p>}
      {current.status === "error" && <div className="hourly-traffic-error" role="alert">
        <p>לא ניתן לטעון את נתוני העומס כרגע</p>
        <button type="button" onClick={() => setRetry(value => value + 1)}>ניסיון נוסף</button>
      </div>}
      {selected && <>
        <div className="hourly-traffic-hour"><Clock3 size={17} aria-hidden="true" /><strong>{dayNames[day - 1]} · <bdi>{clockLabel(hour)}–{clockLabel((hour + 1) % 24)}</bdi></strong></div>
        {selected.available ? <>
          <div className="hourly-traffic-average"><strong>{durationLabel(selected.averageSeconds!)}</strong><span>משך ממוצע שנמדד</span></div>
          <p className={`hourly-traffic-congestion ${selected.congestion}`}><span aria-hidden="true" />{congestionNames[selected.congestion!]}</p>
          <dl className="hourly-traffic-facts">
            <div><dt>תוספת זמן ממוצעת</dt><dd>{selected.delaySeconds === 0 ? "ללא תוספת" : selected.delaySeconds! < 60 ? "פחות מדקה" : durationLabel(selected.delaySeconds!)}</dd></div>
            <div><dt>נהגים שתרמו מידע</dt><dd>{selected.driverCount}</dd></div>
            <div><dt>ימי מדידה</dt><dd>{selected.observedDays}</dd></div>
          </dl>
          {baseline > 0 && <p className="hourly-traffic-baseline">זמן המסלול הבסיסי: {durationLabel(baseline)}</p>}
        </> : <p className="hourly-traffic-empty">עדיין אין מספיק נסיעות בשעה הזו</p>}
      </>}
    </div>

    {data && <>
      <figure className="hourly-traffic-chart">
        <figcaption>משך הנסיעה לאורך היום</figcaption>
        <div className="hourly-traffic-bars" aria-hidden="true" dir="ltr">
          {data.hours.map(item => <span key={item.hour}
            className={`hourly-traffic-bar ${item.available ? item.congestion : "unknown"}${item.hour === hour ? " selected" : ""}`}
            style={item.available ? { height: `${Math.max(8, item.averageSeconds! / maximum * 100)}%` } : undefined}
          />)}
        </div>
        <div className="hourly-traffic-axis" aria-hidden="true" dir="ltr"><span>00</span><span>06</span><span>12</span><span>18</span><span>23</span></div>
        <p className="hourly-traffic-chart-key"><span aria-hidden="true" />קו מקווקו: אין מספיק נתונים. בחרו שעה להצגת הפרטים.</p>
      </figure>
      <button className="hourly-traffic-expand" type="button" aria-expanded={expanded} aria-controls={`${id}-hours`} onClick={() => setExpanded(value => !value)}>
        {expanded ? "הסתרת כל השעות" : "הצגת כל השעות"}
      </button>
      {expanded && <div id={`${id}-hours`} className="hourly-traffic-table-wrap">
        <table className="hourly-traffic-table">
          <caption className="hourly-traffic-sr-only">משך ועומס ממוצעים ב{dayNames[day - 1]} לפי שעה</caption>
          <thead><tr><th scope="col">שעה</th><th scope="col">משך ממוצע</th><th scope="col">עומס</th></tr></thead>
          <tbody>{data.hours.map(item => <tr key={item.hour} className={item.hour === hour ? "selected" : undefined}>
            <th scope="row"><bdi>{clockLabel(item.hour)}</bdi></th>
            <td>{item.available ? durationLabel(item.averageSeconds!) : "אין מספיק נתונים"}</td>
            <td>{item.available ? congestionNames[item.congestion!] : <span aria-label="עומס לא ידוע">—</span>}</td>
          </tr>)}</tbody>
        </table>
      </div>}
    </>}
    <p className="hourly-traffic-note">מוצגים נתונים מנסיעות אמיתיות של נהגים שהסכימו לשיתוף, מ־8 השבועות האחרונים. שעה מוצגת רק כשיש מידע מלפחות {data?.minimumDrivers ?? 3} נהגים. אלה ממוצעים מהעבר; זמן הנסיעה בפועל עשוי להשתנות.</p>
  </section>;
}
